<template>
  <div class="min-h-screen bg-gray-50 pb-20">
    <Head title="Planes de Suscripción" />

    <div class="max-w-[1400px] mx-auto px-6 py-20">
      <!-- Header Moderno -->
      <div class="text-center mb-20">
        <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/10 text-orange-600 text-[10px] font-black uppercase tracking-widest mb-6 border border-orange-200">
          <i class="fas fa-rocket" /> Eleva tu potencia
        </div>
        <h1 class="text-5xl font-black text-gray-900 tracking-tight mb-4">
          El plan ideal para <span class="text-orange-500">tu negocio</span>
        </h1>
        <p class="text-lg text-gray-400 font-medium max-w-2xl mx-auto">
          Gestiona clientes, inventarios y ventas con herramientas de nivel empresarial y soporte dedicado.
        </p>
      </div>

      <!-- Pricing Bento Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
        <!-- Plan 1: Básico -->
        <div class="bg-white rounded-[40px] p-10 border border-gray-100 shadow-sm flex flex-col hover:shadow-2xl hover:shadow-gray-200/50 transition-all duration-500 group">
          <div class="mb-8">
            <h3 class="text-2xl font-black text-gray-900 mb-2 tracking-tight">Gratuito</h3>
            <p class="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-6">Para empezar hoy sin costos</p>
            <div class="flex items-baseline gap-2">
              <span class="text-6xl font-black text-gray-900 tracking-tighter">$0</span>
              <span class="text-sm font-black text-gray-400 uppercase tracking-widest">/mes</span>
            </div>
          </div>
          
          <ul class="space-y-4 mb-10 flex-1">
            <li v-for="feat in freeFeatures" :key="feat" class="flex items-start gap-3 text-sm font-bold text-gray-600">
              <div class="w-6 h-6 rounded-lg bg-gray-50 flex items-center justify-center border border-gray-100 shrink-0 mt-0.5">
                <i class="fas fa-check text-gray-400 text-[10px]" />
              </div>
              <span class="leading-tight">{{ feat }}</span>
            </li>
          </ul>

          <div v-if="account.plan === 'free'" class="w-full py-5 text-center bg-gray-50 rounded-[24px] text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] border border-gray-100 mt-auto">
            <i class="fas fa-check-circle mr-2 text-green-500" /> Tu Plan Actual
          </div>
          <Link v-else href="/subscription/plans" class="w-full py-5 text-center bg-gray-900 text-white rounded-[24px] text-[10px] font-black uppercase tracking-[0.2em] hover:bg-orange-500 hover:-translate-y-1 transition-all active:scale-95 shadow-xl shadow-gray-900/10 mt-auto">
            Seleccionar Plan
          </Link>
        </div>

        <!-- Plan 2: Prime (EL HERO) -->
        <div class="relative rounded-[40px] p-10 bg-gray-100 border border-white shadow-2xl flex flex-col transform lg:-translate-y-8 z-10 group overflow-hidden transition-all duration-500 hover:shadow-orange-500/10">
          <!-- Fondo con ligero gradiente para profundidad -->
          <div class="absolute inset-0 bg-gradient-to-br from-gray-900 to-black" />
          
          <!-- Efecto de Brillo/Glow -->
          <div class="absolute -right-10 -top-10 w-96 h-96 bg-orange-600/30 rounded-full blur-[100px] group-hover:scale-125 transition-transform duration-700" />
          <div class="absolute -left-10 -bottom-10 w-48 h-48 bg-orange-500/10 rounded-full blur-[80px]" />
          
          <div class="absolute top-6 right-8 z-20">
            <span class="px-4 py-1.5 rounded-full bg-orange-500 text-white text-[10px] font-black uppercase tracking-widest shadow-xl shadow-orange-900/50 animate-pulse">
              Recomendado
            </span>
          </div>

          <div class="relative z-10 mb-8">
            <h3 class="text-3xl font-black text-white mb-2 tracking-tight">Prime PRO</h3>
            <p class="text-[10px] font-black text-orange-500 uppercase tracking-[0.3em] mb-6">Máxima Potencia & Automatización</p>
            <div class="flex items-baseline gap-2">
              <span class="text-7xl font-black text-white tracking-tighter drop-shadow-2xl">$30</span>
              <span class="text-sm font-black text-white/60 uppercase tracking-[0.2em]">/mes</span>
            </div>
            <p class="text-xs font-bold text-orange-300/80 mt-1">Aprox. {{ amountInBs }} Bs /mes</p>
          </div>
          
          <ul class="relative z-10 space-y-3.5 mb-10 flex-1">
            <li v-for="feat in proFeatures" :key="feat" class="flex items-start gap-3 text-sm font-bold text-white tracking-wide">
              <div class="w-6 h-6 rounded-lg bg-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/40 shrink-0 mt-0.5">
                <i class="fas fa-check text-white text-[10px]" />
              </div>
              <span class="leading-tight drop-shadow-sm">{{ feat }}</span>
            </li>
          </ul>

          <button
            class="relative z-10 w-full py-5 bg-orange-500 text-white rounded-[24px] text-xs font-black uppercase tracking-[0.3em] shadow-2xl shadow-orange-900/40 hover:bg-orange-600 hover:-translate-y-1 hover:shadow-orange-500/40 transition-all active:scale-95 mt-auto" 
            @click="isPaymentModalOpen = true"
          >
            Solicitar Prime PRO
          </button>
        </div>

        <!-- Plan 3: Enterprise -->
        <div class="bg-white rounded-[40px] p-10 border border-gray-100 shadow-sm flex flex-col hover:shadow-2xl hover:shadow-gray-200/50 transition-all duration-500 group">
          <div class="mb-6">
            <h3 class="text-2xl font-black text-gray-900 mb-2 tracking-tight">Enterprise</h3>
            <p class="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4">Solución a la medida</p>
            <ul class="space-y-2 mb-4">
              <li v-for="feat in enterpriseFeatures" :key="feat" class="flex items-center gap-2 text-xs font-bold text-gray-600">
                <i class="fas fa-check text-orange-500 text-[10px]" />
                <span>{{ feat }}</span>
              </li>
            </ul>
          </div>
          
          <div class="flex-1 space-y-5 mb-8">
            <div class="relative">
              <label class="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2 block leading-none ml-2">Nombre del Negocio</label>
              <input v-model="customForm.businessName" type="text" class="w-full bg-gray-50 border-none rounded-[20px] px-6 py-4 focus:ring-2 focus:ring-orange-500 text-sm font-bold text-gray-900 transition-all" placeholder="Ej. Corporación X" />
            </div>
            <div class="relative">
              <label class="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2 block leading-none ml-2">Requerimientos Especiales</label>
              <textarea v-model="customForm.requirements" rows="3" class="w-full bg-gray-50 border-none rounded-[20px] px-6 py-4 focus:ring-2 focus:ring-orange-500 text-sm font-bold text-gray-900 resize-none transition-all" placeholder="Cuéntanos tus necesidades..." />
            </div>
          </div>

          <button class="w-full py-5 bg-white border-2 border-gray-900 text-gray-900 rounded-[24px] text-[10px] font-black uppercase tracking-[0.2em] hover:bg-gray-900 hover:text-white hover:-translate-y-1 transition-all active:scale-95 mt-auto" @click="submitCustomForm">
            Consultar Cotización
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Pago Moderno (Glassmorphic) -->
    <div v-if="isPaymentModalOpen" class="fixed inset-0 z-50 flex items-center justify-center p-6 backdrop-blur-xl bg-gray-900/60 transition-all duration-300">
      <div class="bg-white rounded-[40px] shadow-2xl w-full max-w-xl overflow-hidden relative border border-white/20 animate-in fade-in zoom-in duration-300">
        <!-- Header del Modal -->
        <div class="p-10 text-center relative overflow-hidden">
          <div class="absolute -right-20 -top-20 w-48 h-48 bg-orange-100 rounded-full blur-3xl opacity-50" />
          <button class="absolute top-6 right-6 w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:text-rose-500 transition-colors" @click="isPaymentModalOpen = false">
            <i class="fas fa-times text-xs" />
          </button>
            
          <div class="w-16 h-16 rounded-2xl bg-gray-900 text-orange-500 flex items-center justify-center mx-auto mb-6 text-2xl shadow-xl">
            <i class="fas fa-paper-plane" />
          </div>
          <h3 class="text-3xl font-black text-gray-900 tracking-tight">Reportar Pago Prime</h3>
          <p class="text-sm font-bold text-gray-400 mt-2 uppercase tracking-widest">Sigue los pasos a continuación</p>
        </div>

        <div class="px-10 pb-10">
          <!-- Selector de moneda -->
          <div class="flex bg-gray-100 p-1 rounded-2xl mb-8">
            <button :class="currency === 'VES' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-900'" class="flex-1 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all" @click="currency = 'VES'">
              Bolívares (Bs)
            </button>
            <button :class="currency === 'USD' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-900'" class="flex-1 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all" @click="currency = 'USD'">
              Dólares ($)
            </button>
          </div>

          <!-- Vista Dólares (Binance) -->
          <div v-if="currency === 'USD'" class="text-center py-10">
            <div class="w-20 h-20 bg-yellow-400/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-yellow-400/20">
              <i class="fab fa-btc text-yellow-500 text-3xl" />
            </div>
            <h4 class="text-xl font-black text-gray-900 mb-2">Pagar con Binance Pay</h4>
            <p class="text-sm font-bold text-gray-400">Próximamente disponible. (Integración pendiente)</p>
          </div>

          <!-- Vista Bolívares -->
          <div v-else>
            <!-- Datos Bancarios Premium -->
            <div class="relative bg-gray-50 border border-gray-200 rounded-[30px] p-6 mb-10 group">
              <button class="absolute top-4 right-4 bg-white shadow-sm border border-gray-100 w-10 h-10 rounded-full flex items-center justify-center text-gray-500 hover:text-orange-500 hover:scale-110 transition-all active:scale-95" title="Copiar Datos" @click="copyBankData">
                <i class="fas" :class="copied ? 'fa-check text-green-500' : 'fa-copy'" />
              </button>
              <div class="grid grid-cols-2 gap-4">
                <div class="flex flex-col">
                  <span class="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Banco</span>
                  <span class="font-black text-gray-900 text-lg">Venezuela (0102)</span>
                </div>
                <div class="flex flex-col">
                  <span class="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">C.I.</span>
                  <span class="font-black text-gray-900 text-lg">26.781.171</span>
                </div>
                <div class="col-span-2 mt-2">
                  <span class="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1 block">Número Pago Móvil</span>
                  <span class="font-black text-gray-900 text-3xl tracking-tighter">0424-4083796</span>
                </div>
              </div>
            </div>

            <!-- Formulario Minimalista -->
            <div class="space-y-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 block">Datos del Pagador</label>
                  <input v-model="bankName" type="text" class="w-full bg-gray-50 border-none rounded-2xl px-5 py-3.5 focus:ring-2 focus:ring-orange-500 font-bold text-sm" placeholder="Ej. Banesco / Juan Perez" />
                </div>
                <div>
                  <label class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 block">Monto Pagado (Bs)</label>
                  <input v-model="amount" type="number" class="w-full bg-gray-50 border-none rounded-2xl px-5 py-3.5 focus:ring-2 focus:ring-orange-500 font-black text-lg" placeholder="25710.00" />
                </div>
              </div>

              <div>
                <label class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 block">Número de Referencia</label>
                <input v-model="paymentReference" type="text" class="w-full bg-gray-100 border-none rounded-2xl px-6 py-5 focus:ring-2 focus:ring-orange-500 text-center font-black text-2xl tracking-[0.3em] uppercase" placeholder="000000" />
              </div>

              <button :disabled="isSubmitting || !paymentReference || !amount || !bankName" class="w-full py-5 bg-gray-900 text-white rounded-[24px] text-xs font-black uppercase tracking-[0.2em] shadow-xl hover:bg-orange-500 transition-all disabled:opacity-30 active:scale-95" @click="submitPayment">
                <span v-if="isSubmitting" class="flex items-center justify-center">Procesando...</span>
                <span v-else>Confirmar Reporte</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import Layout from '@/Shared/Layout'
import { Head, Link } from '@inertiajs/inertia-vue3'

export default defineComponent({
  components: {
    Head,
    Link,
    Layout,
  },

  layout: Layout,

  props: {
    account: Object,
    exchange_rate: {
      type: Number,
      default: 1,
    },
  },

  data() {
    return {
      isPaymentModalOpen: false,
      currency: 'VES',
      paymentReference: '',
      bankName: '',
      amount: null,
      isSubmitting: false,
      copied: false,
      customForm: {
        businessName: '',
        requirements: '',
      },
      freeFeatures: [
        'Clientes ilimitados',
        'Productos ilimitados',
        'Ventas POS ilimitadas',
        'Inventario ilimitado',
        'Compras y despachos ilimitados',
        'Catálogo digital para tus clientes',
        'Multi-moneda (USD / Bs. / COP)',
        'Acceso a marketplace (Próx.)',
      ],
      proFeatures: [
        'Todo lo del Plan Gratuito',
        'Módulo de Nómina y Trabajo a Destajo / Turnos PRO',
        'Módulo de Cobranzas PRO (Portal + WhatsApp)',
        'Reportes de rentabilidad, costos y ganancia real',
        'Alertas de stock mínimo casi por agotarse',
        'Subir compras escaneando fotos con IA',
        'Transportistas propios y seguimiento en despachos',
        'Comandas en tiempo real para restaurantes (Cocina / Bar)',
        'Soporte prioritario 24/7',
      ],
      enterpriseFeatures: [
        'Todo lo del Plan PRO',
        'Múltiples sucursales y depósitos',
        'Integraciones y desarrollo a medida',
        'Asesor y soporte dedicado personalizado',
      ],
    }
  },

  computed: {
    amountInBs() {
      return (30 * (this.exchange_rate || 1)).toFixed(2)
    },
  },

  watch: {
    isPaymentModalOpen(newVal) {
      if (newVal) {
        this.amount = parseFloat(this.amountInBs)
      }
    },
  },

  methods: {
    getWhatsAppLink(plan) {
      const message = `Hola, estoy interesado en el plan ${plan} de RealGestion. ¿Podrías ayudarme con la suscripción?`
      const encodedMessage = encodeURIComponent(message)
      return `https://wa.me/584244010195?text=${encodedMessage}`
    },
    submitCustomForm() {
      const { businessName, requirements } = this.customForm
      const message = `Hola, me gustaría una cotización Custom para mi negocio "${businessName}". Requerimientos: ${requirements}`
      const encodedMessage = encodeURIComponent(message)
      window.open(`https://wa.me/584244010195?text=${encodedMessage}`, '_blank')
    },
    async copyBankData() {
      const data = '0102\n04244083796\n26781171'
      try {
        await navigator.clipboard.writeText(data)
        this.copied = true
        setTimeout(() => { this.copied = false }, 2000)
      } catch (err) {
        console.error('Error copiando el texto: ', err)
      }
    },
    submitPayment() {
      if (!this.paymentReference || !this.amount || !this.bankName) return
            
      this.isSubmitting = true
      this.$inertia.post('/subscription/request', {
        payment_reference: this.paymentReference,
        amount: this.amount, // amount in Bs now
        bank_name: this.bankName,
        currency: this.currency,
        plan: 'sme', // Mapping 'Prime' to 'sme' internally
      }, {
        onSuccess: () => {
          this.isPaymentModalOpen = false
          this.paymentReference = ''
          this.bankName = ''
          this.amount = this.amountInBs
          this.isSubmitting = false
          // Success flash message handles notification
        },
        onError: () => {
          this.isSubmitting = false
        },
      })
    },
  },
})
</script>